import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signInWithPopup, signInWithPhoneNumber } from 'firebase/auth';
import { auth, googleProvider, facebookProvider, getFCMToken, setupRecaptcha } from '../../../lib/firebase';
import { useUserSignupMutation } from '../../../features/api/apiSlice';
import { useDispatch } from 'react-redux';
import { setCredentials, logout } from '../../../features/auth/authSlice';
import {
  PORTAL,
  assertPortalAccess,
  buildAuthUserPayload,
  isProfessionalRole,
} from '../../../lib/authPortals';
import Header from '../../../components/ui/Header';
import Footer from '../../../components/ui/Footer';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { useToast } from '../../../components/ui/Toast';
import AuthPortalSwitcher from '../../../components/ui/AuthPortalSwitcher';
import PasswordLoginForm from '../../../components/auth/PasswordLoginForm';

const ProLogin = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [authMethod, setAuthMethod] = useState('password');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [showVerification, setShowVerification] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [userSignup] = useUserSignupMutation();
  const dispatch = useDispatch();
  const addToast = useToast();

  const defaultRedirect = location.state?.from?.pathname || '/agent-dashboard';

  useEffect(() => {
    document.title = 'Real Estate Agent Login | HireBroker';
    setupRecaptcha('recaptcha-container-pro');
  }, []);

  const getUserLocation = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ latitude: null, longitude: null });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) =>
          resolve({
            latitude: position.coords.latitude.toString(),
            longitude: position.coords.longitude.toString(),
          }),
        () => resolve({ latitude: null, longitude: null }),
        { timeout: 10000 }
      );
    });

  const completeProAuth = async (user, type) => {
    const loc = await getUserLocation();
    const fcmToken = await getFCMToken();

    const response = await userSignup({
      type,
      firebase_id: user.uid,
      name: user.displayName || user.phoneNumber || user.email?.split('@')[0] || 'Agent',
      email: user.email || '',
      mobile: user.phoneNumber || '',
      fcm_id: fcmToken || '',
      latitude: loc.latitude,
      longitude: loc.longitude,
      // Login only — no new pro accounts without explicit role from /pro/register
      portal: 'professional',
    }).unwrap();

    if (response?.error === true) {
      throw new Error(response?.message || 'Real Estate Agent login failed');
    }

    const apiUser = response.user || response.data || {};
    const authUser = buildAuthUserPayload({ apiUser, firebaseUser: user });
    const access = assertPortalAccess(authUser.account_role, PORTAL.PROFESSIONAL);

    if (!access.ok) {
      dispatch(logout());
      try {
        await auth.signOut();
      } catch {
        /* ignore */
      }
      addToast(access.message, 'error');
      return false;
    }

    if (!isProfessionalRole(authUser.account_role)) {
      dispatch(logout());
      addToast(access.message || 'Real Estate Agent account required.', 'error');
      return false;
    }

    dispatch(
      setCredentials({
        user: authUser,
        token: response.token,
        customerId: response.customerId || apiUser.id,
      })
    );
    addToast('Welcome back!', 'success');
    window.location.href = access.redirect || defaultRedirect;
    return true;
  };

  const handleSocialLogin = async (provider) => {
    setIsLoading(true);
    try {
      const authProvider = provider === 'google' ? googleProvider : facebookProvider;
      const result = await signInWithPopup(auth, authProvider);
      await completeProAuth(result.user, provider);
    } catch (err) {
      console.error(err);
      const msg =
        err?.data?.message ||
        err?.message ||
        `${provider} login failed. Please try again.`;
      addToast(msg, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneAuth = async () => {
    if (!phoneNumber.trim()) {
      addToast('Please enter a phone number', 'error');
      return;
    }
    setIsLoading(true);
    try {
      const recaptchaVerifier = setupRecaptcha('recaptcha-container-pro');
      const confirmation = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
      setConfirmationResult(confirmation);
      setShowVerification(true);
    } catch (err) {
      console.error(err);
      addToast('Failed to send verification code.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      addToast('Enter the verification code', 'error');
      return;
    }
    setIsLoading(true);
    try {
      const result = await confirmationResult.confirm(verificationCode);
      await completeProAuth(result.user, 'phone');
    } catch (err) {
      console.error(err);
      addToast('Invalid verification code.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 pt-16">
        <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-10">
          <div className="max-w-md w-full space-y-6">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-secondary rounded-xl flex items-center justify-center">
                  <Icon name="Briefcase" size={32} color="white" />
                </div>
              </div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Real Estate Agent Login</h1>
              <p className="text-muted-foreground">
                Agents, brokers, and property advisors — sign in here
              </p>
            </div>

            <AuthPortalSwitcher mode="login" active="professional" />

            <div className="bg-card border border-border rounded-lg shadow-lg p-6 sm:p-8">
              <div className="flex border-b border-border mb-6 overflow-x-auto">
                <button
                  type="button"
                  onClick={() => setAuthMethod('password')}
                  className={`flex-1 py-2 px-3 text-center text-sm font-medium whitespace-nowrap transition-colors ${
                    authMethod === 'password'
                      ? 'text-secondary border-b-2 border-secondary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Email &amp; Password
                </button>
                <button
                  type="button"
                  onClick={() => setAuthMethod('social')}
                  className={`flex-1 py-2 px-3 text-center text-sm font-medium whitespace-nowrap transition-colors ${
                    authMethod === 'social'
                      ? 'text-secondary border-b-2 border-secondary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Social
                </button>
                <button
                  type="button"
                  onClick={() => setAuthMethod('phone')}
                  className={`flex-1 py-2 px-3 text-center text-sm font-medium whitespace-nowrap transition-colors ${
                    authMethod === 'phone'
                      ? 'text-secondary border-b-2 border-secondary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Phone
                </button>
              </div>

              {authMethod === 'password' && (
                <PasswordLoginForm
                  portal="professional"
                  redirectPath={defaultRedirect}
                  registerPath="/pro/register"
                />
              )}

              {authMethod === 'social' && (
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => handleSocialLogin('google')}
                    disabled={isLoading}
                    className="w-full flex items-center justify-center px-4 py-3 border border-border rounded-md text-sm font-medium bg-background hover:bg-muted disabled:opacity-50"
                  >
                    <Icon name="Chrome" size={18} className="mr-2" />
                    Continue with Google
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSocialLogin('facebook')}
                    disabled={isLoading}
                    className="w-full flex items-center justify-center px-4 py-3 border border-blue-600 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Icon name="Facebook" size={18} className="mr-2" />
                    Continue with Facebook
                  </button>
                </div>
              )}

              {authMethod === 'phone' && (
                <div className="space-y-4">
                  {!showVerification ? (
                    <>
                      <Input
                        label="Phone Number"
                        type="tel"
                        placeholder="+256 7XX XXX XXX"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        disabled={isLoading}
                      />
                      <Button
                        fullWidth
                        size="lg"
                        onClick={handlePhoneAuth}
                        loading={isLoading}
                        disabled={isLoading}
                      >
                        Send Verification Code
                      </Button>
                    </>
                  ) : (
                    <>
                      <Input
                        label="Verification Code"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        disabled={isLoading}
                      />
                      <Button
                        fullWidth
                        size="lg"
                        onClick={handleVerifyCode}
                        loading={isLoading}
                        disabled={isLoading}
                      >
                        Verify & Sign In
                      </Button>
                    </>
                  )}
                </div>
              )}
              <div id="recaptcha-container-pro" style={{ position: 'absolute', left: '-9999px' }} />
            </div>

            <div className="text-center space-y-3">
              <p className="text-sm text-muted-foreground">
                New Real Estate Agent?{' '}
                <Link to="/pro/register" className="text-primary font-medium hover:underline">
                  Create Real Estate Agent account
                </Link>
              </p>
              <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm">
                <p className="text-muted-foreground">
                  Looking for property instead?{' '}
                  <Link to="/login" className="text-primary font-medium hover:underline">
                    User login
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ProLogin;
